/* ============================================
   NewConversationModal — Start a new WhatsApp chat
   Search existing contacts or create a new lead,
   then send the first message via wazzup-send.
   ============================================ */
import { useState, useEffect, useMemo } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/components/layout/Toast'
import { IconSearch, IconWhatsApp, IconUser, IconSend, IconPhone, IconAlertCircle } from '@/components/icons/Icons'
import type { Contacto, Conversacion } from '@/lib/types'

interface NewConversationModalProps {
  conversations: Conversacion[]
  onStarted: (conversacionId: number) => void
  onClose: () => void
}

/** Normalize phone to digits-only, ensure 51 country code prefix */
function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 9) return `51${digits}`
  if (digits.startsWith('51') && digits.length === 11) return digits
  return digits
}

export default function NewConversationModal({ conversations, onStarted, onClose }: NewConversationModalProps) {
  const db = useSupabase()
  const [contactos, setContactos] = useState<Contacto[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedContacto, setSelectedContacto] = useState<Contacto | null>(null)

  // New contact fields (when no existing contact selected)
  const [newPhone, setNewPhone] = useState('')
  const [newName, setNewName] = useState('')

  const [mensaje, setMensaje] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)

  // Load all contacts on mount
  useEffect(() => {
    async function load() {
      try {
        const all = await db.getAll<Contacto>('contactos')
        setContactos(all.sort((a, b) => a.nombre.localeCompare(b.nombre)))
      } catch {
        // Non-critical
      } finally {
        setLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Filter contacts by search
  const filteredContactos = useMemo(() => {
    if (!searchQuery.trim()) return contactos.filter(c => c.telefono)
    const q = searchQuery.toLowerCase()
    return contactos.filter(c => {
      if (!c.telefono) return false
      const name = `${c.nombre} ${c.apellido || ''}`.toLowerCase()
      return name.includes(q) || c.telefono.includes(q) || c.email?.toLowerCase().includes(q)
    })
  }, [contactos, searchQuery])

  // Determine the phone number to use
  const activePhone = selectedContacto?.telefono
    ? normalizePhone(selectedContacto.telefono)
    : newPhone.trim() ? normalizePhone(newPhone) : ''

  // Check if conversation already exists for this phone
  const existingConversation = useMemo(() => {
    if (!activePhone || activePhone.length < 9) return null
    return conversations.find(c =>
      c.canal === 'whatsapp' && c.chat_id === activePhone
    ) || null
  }, [activePhone, conversations])

  // Handle selecting a contact
  const handleSelectContacto = (contacto: Contacto) => {
    if (selectedContacto?.id === contacto.id) {
      setSelectedContacto(null)
    } else {
      setSelectedContacto(contacto)
      setNewPhone('')
      setNewName('')
    }
  }

  // Handle phone input (deselect contact)
  const handlePhoneChange = (value: string) => {
    setNewPhone(value)
    setSelectedContacto(null)
  }

  // Navigate to existing conversation
  const handleGoToExisting = () => {
    if (existingConversation) {
      onStarted(existingConversation.id)
    }
  }

  // Send first message
  const handleSend = async () => {
    if (!activePhone || activePhone.length < 11) {
      showToast('Ingresa un numero de telefono valido', 'error')
      return
    }
    if (!mensaje.trim()) {
      showToast('Escribe un mensaje para iniciar la conversacion', 'error')
      return
    }

    setSending(true)
    try {
      let contactoId = selectedContacto?.id || null

      // If no contact selected, create a new one
      if (!contactoId && newName.trim()) {
        contactoId = await db.add('contactos', {
          nombre: newName.trim(),
          apellido: null,
          email: null,
          telefono: activePhone,
          cargo: null,
          notas: null,
          auth_user_id: null,
          portal_activo: false,
          onboarding_completado: false,
          estado_pipeline: 'lead',
          origen_lead: 'otro',
        })
        showToast('Contacto creado', 'success')
      } else if (!contactoId && !newName.trim()) {
        // No name provided — send without contact
        contactoId = null
      }

      // Send via send-first (handles find-or-create conversation + send)
      const { data: sendResult, error } = await supabase.functions.invoke('wazzup-send', {
        body: {
          chat_id: activePhone,
          canal: 'whatsapp',
          contacto_id: contactoId,
          contenido: mensaje.trim(),
          tipo: 'texto',
        },
      })

      if (error) throw error

      showToast('Mensaje enviado por WhatsApp', 'success')

      // Use the conversation returned from send-first
      const conv = sendResult as { conversacion: { id: number }; mensaje: unknown; wazzup_success: boolean } | null
      if (conv?.conversacion?.id) {
        onStarted(conv.conversacion.id)
      } else {
        // Fallback: search by chat_id (GET /chat/conversaciones now supports this)
        const { data: convData } = await supabase
          .from('conversaciones')
          .select('id')
          .eq('canal', 'whatsapp')
          .eq('chat_id', activePhone)
          .single()

        const found = convData as { id: number } | null
        if (found) {
          onStarted(found.id)
        } else {
          onClose()
        }
      }
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Error al enviar mensaje',
        'error'
      )
    } finally {
      setSending(false)
    }
  }

  const phoneValid = activePhone.length >= 11
  const canSend = phoneValid && mensaje.trim() && !existingConversation

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal new-conv-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconWhatsApp size={20} /> Nueva Conversacion
          </h2>
          <button className="modal-close" onClick={onClose} type="button">
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Search contacts */}
          <div className="form-group">
            <label className="form-label">Buscar contacto</label>
            <div className="new-conv-search">
              <IconSearch size={16} />
              <input
                type="text"
                placeholder="Nombre, telefono o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Contact list */}
          {!loading && filteredContactos.length > 0 && (
            <div className="new-conv-contact-list">
              {filteredContactos.slice(0, 8).map((c) => (
                <div
                  key={c.id}
                  className={`new-conv-contact-item ${selectedContacto?.id === c.id ? 'new-conv-contact-item--selected' : ''}`}
                  onClick={() => handleSelectContacto(c)}
                >
                  <div className="new-conv-contact-item__icon">
                    <IconUser size={16} />
                  </div>
                  <div className="new-conv-contact-item__info">
                    <span className="new-conv-contact-item__name">
                      {c.nombre}{c.apellido ? ` ${c.apellido}` : ''}
                    </span>
                    <span className="new-conv-contact-item__phone">
                      <IconPhone size={11} /> {c.telefono}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Cargando contactos...
            </div>
          )}

          {/* New phone (only when no contact selected) */}
          {!selectedContacto && (
            <>
              <div className="new-conv-divider">
                <span>o escribe un numero nuevo</span>
              </div>

              <div className="new-conv-phone-row">
                <div className="new-conv-phone-prefix">+51</div>
                <input
                  className="form-input"
                  type="tel"
                  placeholder="987 654 321"
                  value={newPhone}
                  onChange={(e) => handlePhoneChange(e.target.value.replace(/\D/g, '').slice(0, 9))}
                  maxLength={9}
                />
              </div>

              {newPhone.length > 0 && (
                <div className="form-group" style={{ marginTop: 8 }}>
                  <input
                    className="form-input"
                    placeholder="Nombre del contacto (opcional)"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
              )}
            </>
          )}

          {/* Existing conversation warning */}
          {existingConversation && (
            <div className="new-conv-existing-badge">
              <IconAlertCircle size={16} />
              <div>
                <strong>Ya existe una conversacion con este numero</strong>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleGoToExisting}
                  type="button"
                  style={{ marginLeft: 12 }}
                >
                  Ir a conversacion
                </button>
              </div>
            </div>
          )}

          {/* First message */}
          {phoneValid && !existingConversation && (
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label">Primer mensaje</label>
              <textarea
                className="form-input new-conv-textarea"
                placeholder="Hola! Te escribo de 1Conecta..."
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} type="button">
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={!canSend || sending}
            type="button"
          >
            {sending ? (
              <>Enviando...</>
            ) : (
              <><IconSend size={14} /> Enviar WhatsApp</>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
